/* JSON LD */
import { LocalBusiness, MerchantReturnPolicy, Organization, WebSite, WithContext } from 'schema-dts'
import { JsonLd } from 'src/components/JsonLd'

import config from 'src/config'
import shopInfos from 'template/infos'
const {
  name, legalname,
  url, title, description, priceRange,
  email, telephone, fax:faxNumber, 

  street:streetAddress, 
  locality:addressLocality, postalcode:postalCode,
} = shopInfos
const shopUrl = url.replace(/\/$/, '')
const merchantReturnPolicyId = `${shopUrl}#merchant-return-policy`
const organizationId = `${shopUrl}#organization`
const localBusinessId = `${shopUrl}#local-business`

type ShopInfosWithReviews = typeof shopInfos & {
  googlePlaceId?: string
  googleReviewHref?: string
}

const reviewInfos = shopInfos as ShopInfosWithReviews

const merchantReturnPolicyCountries = config.structuredData.returns.applicableCountries?.length
  ? config.structuredData.returns.applicableCountries
  : [config.structuredData.shipping.destinationCountry]

const merchantReturnPolicy = {
  '@context': 'https://schema.org',
  '@type': 'MerchantReturnPolicy',
  '@id': merchantReturnPolicyId,
  applicableCountry: merchantReturnPolicyCountries,
  returnPolicyCountry: config.structuredData.returns.returnPolicyCountry || 'DE',
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: config.structuredData.returns.merchantReturnDays,
  refundType: config.structuredData.returns.refundType,
  returnMethod: config.structuredData.returns.returnMethod,
  returnFees: config.structuredData.returns.returnFees,
  merchantReturnLink: `${shopUrl}/widerrufsbelehrung`,
} as WithContext<MerchantReturnPolicy>

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

export const SeoStructuredData: React.FC<{ logoUrl?: string }> = ({ logoUrl }) => <>

  {/* ----------------------------------------------------------------------- */}

  <JsonLd<WebSite> item={{
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${shopUrl}#website`,

    url,
    name: shopInfos.titleAlternate || name,
    alternateName: name,
    inLanguage: 'de-DE',
    publisher: { '@id': organizationId },

    potentialAction: {
      '@type': 'SearchAction',
      target: `${url}suche?suche={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },

  } as WithContext<WebSite>} />

  {/* ----------------------------------------------------------------------- */}

  <JsonLd<Organization> item={{
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId,

    name: legalname || name || title,
    legalName: legalname,
    alternateName: name,
    url,
    logo: logoUrl,
    email,
    telephone,
    hasMerchantReturnPolicy: { '@id': merchantReturnPolicyId },
    sameAs: reviewInfos.googleReviewHref ? [reviewInfos.googleReviewHref] : undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality,
      postalCode,
      addressCountry: 'DE',
    },
  }} />

  {/* ----------------------------------------------------------------------- */}

  {/* Only a configured physical location is described as a local business.
      A legal correspondence address alone does not establish a showroom. */}
  {reviewInfos.googlePlaceId && <JsonLd<LocalBusiness> item={{
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': localBusinessId,

    url,
    name: legalname || name,
    legalName: legalname,

    slogan: description,
    priceRange,
    parentOrganization: { '@id': organizationId },

    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality,
      postalCode,
      addressCountry: 'DE',
      telephone,
      faxNumber,
    },

    telephone,
    faxNumber,
    email,
    sameAs: reviewInfos.googleReviewHref ? [reviewInfos.googleReviewHref] : undefined,
    hasMerchantReturnPolicy: { '@id': merchantReturnPolicyId },

  } as WithContext<LocalBusiness>} />}

  {/* ----------------------------------------------------------------------- */}

  <JsonLd<MerchantReturnPolicy> item={merchantReturnPolicy} />

  {/* ----------------------------------------------------------------------- */}

</>

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
